/**
 * Kabuğun metinleri: giriş ekranı, üst şerit, genel bakış ve müşteri listesi.
 *
 * ## Neden burada
 *
 * `App.tsx` ürün sahibinin ilk gördüğü ekran. Giriş ekranındaki her cümle İngilizceydi ("Sign
 * in", "SECURE WORKSPACE", "Today at a glance") ve hepsi koda gömülüydü. Metin buraya taşındı:
 * bir cümleyi değiştirmek artık kod değiştirmek değil, ve hangi cümlenin nerede olduğu tek
 * dosyada görünüyor. Desen `ortak.ts` içinde anlatılıyor.
 *
 * Kelime seçimleri `docs/tr-sozluk.md` içinde karara bağlı: workspace çalışma alanı, fulfilment
 * operasyon, reconciliation mutabakat, hand-off devir, oversight gözetim.
 */

/**
 * Pano sekmelerinin adları.
 *
 * Ürün sahibi: *"Operasyon Panosu yerine operasyon yaz direkt oyle bu panosu su panosu olmaz"*.
 * Dört ad da tek kelimeye indi. "Panosu" eki hiçbir şey anlatmıyordu: kullanıcı zaten bir panoya
 * bakıyor, ve her sekmeye aynı eki yazmak dört adı da uzatıp hiçbirini ayırt etmiyordu.
 *
 * Uçtan gelen `label` İngilizce ("Sales") ve gezinme çubuğunda bir de iş akışı bağlantısı var;
 * iki bağlantının adı birbirinin içinde geçemez, çünkü ekranı okuyan da ekranı test eden de
 * hangisine tıkladığını bilmeli. Ayrım kelime sayısında: pano bağlantıları tek kelime (Satış),
 * iş akışı bağlantıları iki kelime (Satış işleri).
 */
export const BOLUM_ADI: Readonly<Record<string, string>> = {
  SALES: 'Satış',
  OPERATIONS: 'Operasyon',
  FINANCE: 'Finans',
  MARKETING: 'Pazarlama',
  // Beşinci bölüm (30.08.2026). "İnsan Kaynakları" değil: kural tek kelime ve bu iki kelimeydi.
  HR: 'İK',
};

/**
 * Rolün çalışma alanı: adı ve tek cümlelik tarifi.
 *
 * Eskiden her rolün bir de kendi `accent` rengi vardı, çivit, mavi, kehribar, pembe, ve hiçbiri
 * MBD'nin paletinde yoktu. Ürün sahibi *"sitedeki ui kutuphanesi kullanilacak"* dedi; o renkler
 * kaldırıldı. Rolü ayıran şey artık renk değil, ekranın adı ve yanındaki rozet.
 *
 * Tarif cümlesi rolün ne yaptığını söylüyor, ne olduğunu değil: kullanıcı zaten kim olduğunu
 * biliyor, bilmediği şey bu ekranda ne yapabildiği.
 */
export const ROL_CALISMA_ALANI: Readonly<Record<string, { baslik: string; tarif: string }>> = {
  ADMIN: {
    baslik: 'Yönetim çalışma alanı',
    tarif: 'Kullanıcılar, yapılandırma ve değiştirilemeyen denetim izi.',
  },
  CRM_SALES: {
    baslik: 'Satış çalışma alanı',
    tarif: 'Talebi nitele, teklifi biçimlendir, kabul edilen işi siparişe çevir.',
  },
  OPERATIONS: {
    baslik: 'Operasyon çalışma alanı',
    tarif: 'Her kalemi teslim et, arkasında açık bir operasyon izi bırak.',
  },
  FINANCE: {
    baslik: 'Finans çalışma alanı',
    tarif: 'Kanıtı doğrula, maliyeti denetle, siparişi mutabakata bağla.',
  },
  MANAGER: {
    baslik: 'Müdür çalışma alanı',
    tarif: 'İstisnaları bölümler arası gözetimle çöz.',
  },
  HR: {
    baslik: 'İK çalışma alanı',
    tarif: 'Kadroyu gör: kim var, hangi rolde, hesabı açık mı.',
  },
  READ_ONLY: {
    baslik: 'Salt okunur çalışma alanı',
    tarif: 'Bugünkü durumu değiştirmeden incele.',
  },
};

export const KABUK_METIN = {
  /** Kelime markası, görsel değil: v3'te MBD logosu diye bir dosya yok. */
  marka: {
    serit: 'MBD TRAVEL · OPERASYON SİSTEMİ',
    /** Logonun alternatif metni: görsel yüklenmezse marka adı okunur. */
    logoAlt: 'MBD Travel',
    ad: 'MBD Travel',
    alt: 'CRM',
  },

  /** Açılışta oturum denetlenirken görünen tek ekran. */
  acilis: {
    yukleniyor: 'Çalışma alanınız yükleniyor…',
  },

  giris: {
    baslik: 'İlk temastan mutabakata kadar tek ve güvenilir bir iz.',
    tarif: 'Role göre satış, operasyon ve finans denetimleri; her devirde açık kanıt.',
    /** Ekran yalan söylemiyor: dış servisler bağlı değilse bunu ekranın kendisi yazıyor. */
    ortam: 'Yerel ortam · dış servisler tanımlı değil',
    ustBaslik: 'GÜVENLİ ÇALIŞMA ALANI',
    formBaslik: 'Giriş yap',
    formTarif: 'Hesabınız hangi çalışma alanını ve hangi işlemleri görebileceğinizi belirler.',
    eposta: 'E-posta',
    parola: 'Parola',
    gonderiliyor: 'Giriş yapılıyor…',
    devam: 'Devam',
    hata: 'Giriş yapılamadı. Yerel API çalışıyor mu, denetleyip yeniden deneyin.',
  },

  ust: {
    ortam: 'Yerel ortam',
    gezinme: 'Çalışma alanı gezinmesi',
    cikis: 'Çıkış',
  },

  /**
   * Gezinme bağlantıları.
   *
   * İş akışı bağlantıları bilerek iki kelime: pano bağlantısı "Satış", iş akışı bağlantısı
   * "Satış işleri". Biri diğerinin adının içinde geçmiyor, o yüzden hem okuyan hem test
   * hangisine tıkladığını biliyor.
   */
  /**
   * Menü adları.
   *
   * Her ad **bir kez** geçiyor. Eskiden dört pano bölümünün yanında dört akış bağlantısı daha
   * duruyordu ("Satış" ile "Sales", "Finans" ile "Finance") ve ikisi aynı işin iki kapısıydı.
   * Akış panelleri kendi bölümlerinin sayfasına taşındı; menüde tek kapı kaldı.
   *
   * "Gözetim" değil **"Denetim"**: bölümler arası istisna kuyruklarını okuyan ekran bir iş
   * bölümü değil, dördünün üstünde duran bir yönetim ekranı.
   */
  gezinme: {
    yonetim: 'Yönetim',
    altMenu: 'alt menüsü',
    genelBakis: 'Genel Bakış',
    musteriler: 'Müşteriler',
    denetim: 'Denetim',
    v3: 'Web sitesi',
    /** Grubun ilk alt öğesi: grup adını tekrar etmez; en üstteki "Genel Bakış" menüde tek (Ürün Sahibi, 14.10.2026). */
    bolumSatis: 'İşlemler',
    bolumOperasyon: 'İş kuyruğu',
    bolumPazarlama: 'Özet',
    tedarikciler: 'Tedarikçiler',
    belgeler: 'Belgeler',
    kullanicilar: 'Kullanıcılar',
    /** Sunucuya yazılan sistem ayarları; kişisel tercihler hesap menüsündeki "Ayarlar"da. */
    yapilandirma: 'Yapılandırma',
    /**
     * P1-6 (denetim, 07.09.2026): bölüm listesi çekilemezse eskiden sessizce `null`e
     * dönüyordu ve bütün bölüm bağlantıları (Satış, Operasyon, Finans, Pazarlama, İK) menüden
     * kayboluyordu; kullanıcı bunu "rolümün yetkisi yok" sanıyordu, oysa istek başarısız olmuştu.
     */
    bolumlerYuklenemedi: 'Bölüm listesi yüklenemedi; menüde bölüm bağlantıları eksik olabilir.',
  },
  /** Hesap menüsü: kişiye tıklayınca sağda açılan üç madde. */
  hesap: {
    ac: 'Hesap menüsü',
    profil: 'Profil',
    ayarlar: 'Ayarlar',
    cikis: 'Çıkış',
    /** P1-6: çıkış isteği başarısız olursa (ör. 503) eskiden hiçbir şey görünmüyordu. */
    cikisHata: 'Çıkış işlemi başarısız oldu. Yeniden deneyin.',
  },

  calismaAlani: {
    /** Rozetin yanındaki üst yazı: "SATIŞ ÇALIŞMA ALANI". Rol adı büyük harfe tr-TR ile geçiyor. */
    ustBaslikEki: 'ÇALIŞMA ALANI',
    varsayilanBaslik: 'Çalışma alanı',
    hata: 'Çalışma alanı yüklenemedi.',
  },

  /**
   * Hata sınırı: bir bileşenin çizim sırasında attığı hatayı yakalayan iki katman.
   *
   * Ölçüldü: `web/src` içinde hiç `ErrorBoundary`/`componentDidCatch` yoktu. Tek bir çizim
   * hatası (`Workflow.tsx`, tanımsız bir alan okuması) girişi, paneli ve müşteri listesini
   * birden beyaz ekrana düşürüyordu; kurtarma yolu sayfayı elle yenilemekti.
   *
   * İki katman ayrı metin taşıyor çünkü ikisinin gerçeği farklı: uygulama katmanı çöktüğünde
   * hiçbir şey ayakta değildir ("sayfayı yenile" tek gerçek çıkış), bölüm katmanı çöktüğünde
   * üst şerit ve menü ayaktadır ("ana sayfaya dön" gerçek bir seçenek).
   */
  hataSiniri: {
    uygulamaBaslik: 'Ekran çizilemedi',
    uygulamaGovde:
      'Bir bileşen beklenmeyen bir hatayla durdu, uygulama bu noktada durdu. Veri kaybolmadı: sayfa yenilendiğinde iş kaldığı yerden devam eder.',
    bolumBaslik: (bolum: string) => `${bolum} çizilemedi`,
    bolumGovde:
      'Bu bölümün ekranı beklenmeyen bir hatayla durdu. Üst menü ve diğer bölümler bundan etkilenmedi.',
    yenidenDene: 'Yeniden dene',
    sayfayiYenile: 'Sayfayı yenile',
    anaSayfayaDon: 'Ana sayfaya dön',
    teknikAyrinti: 'Teknik ayrıntı',
    gunlukNotu: 'Bu ayrıntı tarayıcının konsoluna ve yerel hata günlüğüne de yazıldı.',
  },

  genelBakis: {
    ustBaslik: 'CANLI VERİTABANI GÖRÜNÜMÜ',
    baslik: 'Bugüne bakış',
    guncel: 'Güncel',
    /** Her kartın altında sayının nereden geldiği yazıyor: kart bir iddia değil bir ölçüm. */
    kaynak: 'Kaynak:',
  },

  musteriler: {
    ustBaslik: 'MÜŞTERİ REHBERİ',
    baslik: 'Müşteriler',
    araBaslik: 'Müşteri ara',
    araIpucu: 'Ad, e-posta ya da telefon',
    sutunAd: 'Ad',
    sutunTur: 'Tür',
    sutunDurum: 'Durum',
    sutunEposta: 'E-posta',
    sutunKrediLimiti: 'Kredi limiti',
    bosBaslik: 'Müşteri bulunamadı',
    bosTarif: 'Daha geniş bir ad, e-posta ya da telefon araması deneyin.',
    yukleniyor: 'Müşteriler yükleniyor…',
    sayfaEki: 'müşteri',
    satiriAc: (ad: string) => `${ad} müşterisinin ayrıntılarını aç`,

    /**
     * Hızlı müşteri ekleme: satış akışı vakasının birinci adımı
     * (`docs/crm/satis-akisi-vaka.md`). Ürün Sahibi'nin kendi cümlesi: "Sales hızlıca müşteri
     * detaylarını girer: isim, soyisim, tel, mümkünse email." Form dört alanı zorunlu tutuyor:
     * ad, soyad, telefon; e-posta opsiyonel.
     *
     * TC Kimlik No beşinci, doldurulabilir ama **boş bırakılabilir** bir alan. Düzeltme
     * (26.08.2026, Ürün Sahibi kararı): "TC numarası vermeyebilir, zorunlu yapmasın." Kural
     * silinmedi, taşındı: kayıt anında istenmiyor, B2C fatura kesiminde isteniyor
     * (`finance/invoice.readiness.ts`), o yüzden ipucu bunu satışçıya baştan söylüyor.
     */
    hizliEkle: {
      acDugmesi: 'Hızlı müşteri ekle',
      baslik: 'Hızlı müşteri ekle',
      tarif: 'Müşteri telefonda bekliyor: ad, soyad, telefon, mümkünse e-posta.',
      adEtiketi: 'Ad',
      soyadEtiketi: 'Soyad',
      telefonEtiketi: 'Telefon',
      epostaEtiketi: 'E-posta',
      epostaIpucu: 'Zorunlu değil',
      tcKimlikEtiketi: 'TC Kimlik No',
      tcKimlikIpucu: 'Zorunlu değil, sonra da tamamlanabilir. Fatura kesilirken (B2C) isteniyor.',
      kaydet: 'Kaydet',
      kaydediliyor: 'Kaydediliyor…',
      vazgec: 'Vazgeç',
      basarili: (ad: string) => `${ad} kaydedildi.`,
      mukerrerBaslik: 'Bu müşteri zaten kayıtlı olabilir',
      mukerrerTarif: 'Girdiğiniz telefon ya da e-postayla eşleşen kayıt var. Yeni bir kayıt açmadan önce kontrol edin.',
      mukerrerGoster: 'Kayda git',
      mukerrerYineDeKaydet: 'Bu farklı bir müşteri, mükerrer değil',
      dogrulamaZorunlu: 'Ad, soyad ve telefon zorunlu.',
      dogrulamaTcKimlik: 'TC Kimlik No girildiyse 11 hane olmalı ve ilk hanesi sıfır olamaz.',
      dogrulamaEposta: 'E-posta adresi doğru biçimde değil.',
    },
  },

  /**
   * Müşteri detayı: telefon, notlar, sipariş geçmişi — üç ayrı gönderimde ölçülen kusur.
   * Satır tıklanmıyordu, bu üçü hiçbir yerde görünmüyordu.
   */
  musteriDetay: {
    baslik: 'Müşteri detayı',
    baslikOnek: 'MÜŞTERİ',
    geriDon: 'Müşterilere dön',
    bulunamadi: 'Müşteri bulunamadı.',
    sutunTur: 'Tür',
    sutunDurum: 'Durum',
    eposta: 'E-posta',
    telefon: 'Telefon',
    tcKimlik: 'TC Kimlik No',
    vergiNo: 'Vergi/ticaret sicil no',
    faturaBasligi: 'Fatura bilgisi',
    faturaTarif: 'Satış faturası bu bilgilerle kesilir. Eksikse fatura kuyrukta bekler.',
    faturaAdresi: 'Fatura adresi',
    faturaAdresiIpucu: 'Tek satır, en fazla 140 karakter',
    ulkeKodu: 'Ülke kodu (iki harf)',
    pasaport: 'Pasaport no (TC yoksa)',
    faturaKaydet: 'Fatura bilgisini kaydet',
    faturaKaydediliyor: 'Kaydediliyor',
    faturaGrupAdres: 'Adres ve ülke',
    faturaGrupKimlik: 'Kimlik',
    ulkeKoduIpucu: 'Örnek: TR, AE',
    faturaYalnizOkuma: 'Fatura bilgisini düzenlemek için müşteri güncelleme yetkisi gerekir.',
    teklifOlustur: 'Teklif oluştur',
    notYaz: 'Not yaz',
    hizliEylemler: 'Hızlı eylemler',
    finansBasligi: 'Finans bilgisi',
    krediLimitiYok: 'Tanımlı değil',
    kanalMeta: (baglayan: string, tarih: string) => `${baglayan} · ${tarih}`,
    faturaKaydedildi: 'Fatura bilgisi kaydedildi.',
    sahip: 'Sahibi',
    olusturulma: 'Oluşturulma',
    guncellenme: 'Son güncelleme',
    notlarBasligi: 'Notlar',
    notYok: 'Henüz not yok.',
    notSatiri: (govde: string, tarih: string) => `${govde} · ${tarih}`,
    notEkleEtiketi: 'Yeni not',
    notIpucu: 'Bu müşteriyle ilgili olgusal bir bağlam yazın',
    notEkle: 'Not ekle',
    notBos: 'Bir not yazın.',
    notEklendi: 'Not eklendi.',
    siparislerBasligi: 'Sipariş geçmişi',
    siparisYok: 'Henüz sipariş yok.',
    siparisBosYardim: 'Sipariş oluşunca burada listelenir.',
    // Satış özeti (03.11.2026): teklifler müşteriye bağlı, harcama yalnız tahsil edilen paradan.
    satisOzetiBasligi: 'Satış özeti',
    satisOzetiYuklenemedi: 'Satış özeti yüklenemedi.',
    toplamTeklif: 'Toplam teklif',
    kabulEdilen: 'Kabul edilen',
    kabulOrani: 'Kabul oranı',
    kabulOraniYok: 'Henüz iletilmiş teklif yok',
    kabulOraniIpucu: 'Kabul edilen teklifler, taslak ve iptal dışındaki tekliflere bölünür.',
    acikTeklif: 'Açık, yanıt bekleyen',
    reddedilenSuresiDolan: 'Reddedilen, süresi dolan',
    basariliSatis: 'Başarılı satış',
    basariliSatisIpucu: 'Kabul edilen teklif ya da ödemesi alınmış sipariş.',
    ilkSatis: 'İlk satış',
    sonSatis: 'Son satış',
    harcamaBasligi: 'Harcama (tahsil edilen)',
    harcamaIpucu: 'Yalnız doğrulanmış ödemeler, onaylı iadeler düşülmüş, iptal siparişler hariç. Para birimleri ayrı gösterilir, çevrilmez.',
    harcamaYok: 'Bu müşteriden henüz tahsil edilmiş ödeme yok.',
    harcamaGizli: 'Harcama tutarları finans yetkisi gerektirir. Görmek için yöneticinizden yetki isteyin.',
    sutunParaBirimi: 'Para birimi',
    sutunTahsil: 'Tahsil edilen',
    sutunKabulSatis: 'Sipariş tutarı',
    sutunSiparisSayisi: 'Sipariş',
    sutunOrtalama: 'Ortalama sipariş',
    tekliflerBasligi: 'Teklifler',
    teklifYok: 'Henüz teklif yok.',
    teklifBosEylem: 'Özel tatil paketi oluştur',
    sutunKod: 'Kod',
    sutunUrun: 'Ürün / başlık',
    sutunTarih: 'Tarih',
    sutunDurumu: 'Durum',
    teklifiAc: 'Teklifi aç',
    sutunOdeme: 'Ödeme',
    sutunTahsilat: 'Tahsil edilen',
    siparisiAc: 'Siparişi aç',
    odemeDurumu: {
      UNPAID: 'Ödeme yok',
      RECORDED: 'Ödeme kaydedildi, doğrulanmadı',
      PARTIAL: 'Kısmen ödendi',
      PAID: 'Ödendi',
      PARTIALLY_REFUNDED: 'Kısmen iade edildi',
      REFUNDED: 'İade edildi',
    } as Readonly<Record<string, string>>,
    oranYaz: (baz: number) => `%${(baz / 100).toLocaleString('tr-TR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`,
    /**
     * İzin adı ekranda **geçmiyor**: operatörün elinde `customer:read_financial` diye bir kelimeyle
     * yapabileceği bir şey yok, isteyeceği kişi ise bellidir. `metin-tamlik` korkuluğu bu satırı
     * "çevrilmeden kalmış metin" olarak sayıyordu ve haklıydı.
     */
    financialGizli: 'Finansal ayrıntılar gizli: bu alanları görmek için yöneticinizden yetki isteyin',
    krediLimiti: 'Kredi limiti',
    finansNotu: 'Finans notu',
    // CRM-B4: GeziPuan defteri. Bakiye üç parçalı: harcanabilir, henüz olgunlaşmamış (bekleyen)
    // ve yakında sona erecek. Kural: puan **kazanıldığında** deftere yazılır (earnedAt); harcanabilir
    // olması ayrı bir olay (maturesOn). İkisini tek tarih göstererek karıştırmak yanlış bilgi verir.
    gezipuanBasligi: 'GeziPuan',
    gezipuanGizli: 'GeziPuan defteri gezipuan:read yetkisi istiyor',
    gezipuanHarcanabilir: 'Harcanabilir',
    gezipuanBekleyen: 'Bekleyen (seyahat henüz tamamlanmadı)',
    gezipuanYakindaSuresiDolacak: 'Yakında süresi dolacak',
    gezipuanHeldeki: 'Harcama beklemesinde',
    gezipuanYok: 'Bu müşterinin GeziPuan hareketi yok.',
    gezipuanHareketlerBasligi: 'GeziPuan hareketleri',
    sutunTarihYazildi: 'Deftere yazıldığı tarih',
    sutunHareketTuru: 'Tür',
    sutunTutar: 'Tutar',
    sutunSiparis: 'Sipariş',
    // CRM-B9: kanal kimliği. Müşteriye hangi kanaldan (WhatsApp, Instagram, telefon, e-posta)
    // ulaşılabildiğinin kaydı. Chatwoot bağlı değil, bağlantı elle kuruluyor (kapsam.md §1).
    kanalKimligiBasligi: 'Kanal kimliği',
    kanalKimligiGizli: 'Kanal kimliği channel_identity:read yetkisi istiyor',
    kanalYok: 'Bu müşteriye bağlı kanal kimliği yok.',
    sutunKanal: 'Kanal',
    sutunHandle: 'Kullanıcı adı / numara',
    sutunKanalDurum: 'Durum',
    sutunBaglayan: 'Bağlayan',
    sutunBaglanmaTarihi: 'Bağlanma tarihi',
    kanalEkleEtiketiKanal: 'Kanal',
    kanalEkleEtiketiHandle: 'Kullanıcı adı veya numara',
    kanalEkle: 'Kanal ekle',
    kanalHandleBos: 'Bir kullanıcı adı veya numara yazın.',
    kanalEklendi: 'Kanal eklendi.',
    kanalYazmaGizli: 'Yeni kanal eklemek channel_identity:write yetkisi istiyor',
  },

  /** Sipariş detayı: kuyruk satırları ve sipariş kodları artık buraya bağlanıyor. */
  siparisDetay: {
    baslik: 'Sipariş detayı',
    baslikOnek: 'SİPARİŞ',
    geriDon: 'Siparişlere dön',
    bulunamadi: 'Sipariş bulunamadı.',
    musteri: 'Müşteri',
    durum: 'Durum',
    toplam: 'Toplam',
    surum: 'Kabul edilen teklif sürümü',
    kalemlerBasligi: 'Kalemler',
    sutunKalemTuru: 'Tür',
    sutunAciklama: 'Açıklama',
    sutunTedarikci: 'Tedarikçi',
    sutunAdet: 'Adet',
    sutunBirimFiyat: 'Birim fiyat',
    sutunToplam: 'Satır toplamı',
    gorevlerBasligi: 'Operasyon görevleri',
    gorevYok: 'Bu siparişin operasyon görevi yok.',
    gorevSatiri: (kod: string, durum: string) => `${kod} · ${durum}`,
    mutabakatBasligi: 'Mutabakat',
    mutabakatYok: 'Bu siparişin mutabakat kaydı henüz yok.',
  },
} as const;
